import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/env';
import { cn } from '../../utils/cn';

const Register: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const isValidUsername = (username: string): boolean => {
    return username.trim().length >= 3 && username.trim().length <= 50;
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPassword = (password: string): boolean => {
    if (password.length < 8) return false;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasDigit = /\d/.test(password);
    return hasLetter && hasDigit;
  };

  const isValidWalletAddress = (address: string): boolean => {
    const ethAddressRegex = /^0x[a-fA-F0-9]{40}$/;
    return ethAddressRegex.test(address);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!username.trim() || !email.trim() || !password.trim() || !walletAddress.trim()) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    if (!isValidUsername(username)) {
      setError('Username must be between 3 and 50 characters long');
      setIsLoading(false);
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    if (!isValidPassword(password)) {
      setError('Password must be at least 8 characters long and contain both letters and numbers');
      setIsLoading(false);
      return;
    }

    if (!isValidWalletAddress(walletAddress)) {
      setError('Please enter a valid ERC-20 wallet address (0x followed by 40 hexadecimal characters)');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(API_ENDPOINTS.REGISTER, {
        username: username,
        email: email,
        password: password,
        usdcWallet: walletAddress
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        navigate('/login', { 
          state: { 
            message: 'Registration successful! Please log in with your credentials.',
            prefilledUsername: username
          } 
        });
      } else {
        setError('Registration failed. Please try again.');
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.data?.expandedError?.message) {
          setError(err.response.data.expandedError.message);
        } else if (err.response?.status === 400) {
          setError('Invalid registration data. Please check your information.');
        } else if (err.response?.status === 409) {
          setError('An account with this email already exists.');
        } else if (err.response?.status && err.response.status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError('Registration failed. Please try again.');
        }
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = cn(
    'w-full rounded-xl border-0 bg-[#eeeeee] px-4 py-3 text-gray-900',
    'placeholder:text-gray-400',
    'focus:outline-none focus:ring-2 focus:ring-sky-500/30'
  );

  const labelClass = 'text-sm font-bold text-gray-900';
  const hintClass = 'text-xs text-gray-500';

  return (
    <div
      className={cn(
        'min-h-screen homeBg',
        'bg-zinc-950 bg-gradient-to-r from-zinc-950 via-slate-600/35 to-zinc-950',
        'px-4 py-10 sm:px-6 sm:py-12 lg:px-8'
      )}
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-6 lg:flex-row lg:items-stretch lg:justify-center lg:gap-8">
        <section
          className={cn(
            'flex flex-1 flex-col rounded-[40px] bg-[#f9f9f9] p-8 shadow-sm sm:p-10 lg:max-w-xl lg:p-12 blurLayerInteractive'
          )}
        >
          <p className="text-xs font-medium uppercase tracking-wider text-gray-400">
            Business payment workspace
          </p>
          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-gray-900 sm:text-4xl">
            Make complex payment operations feel obvious from the first click.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-gray-600">
            ZK Gate helps teams collect, settle, and reconcile crypto payouts without losing visibility—so finance and operations stay aligned from the first transfer to the last mile.
          </p>
          <div className="mt-8 rounded-2xl bg-gray-100/50 p-6 sm:p-8">
            <h2 className="text-sm font-semibold text-gray-900">
              What your team gets right away
            </h2>
            <ul className="mt-4 flex flex-col gap-3">
              {[
                'Create payment links, invoices, and direct crypto collection without exposing the settlement complexity underneath.',
                'Route settlement through the hosted smart-contract service with live visibility into payout progress and exceptions.',
                'Keep operators focused on action, not troubleshooting, with one place for review, retry, and follow-up.',
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm text-gray-600">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500"
                    aria-hidden
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          className={cn(
            'flex w-full flex-col rounded-[40px] bg-[#f9f9f9] p-8 shadow-sm sm:p-10 lg:max-w-md lg:flex-none lg:p-12 blurLayerInteractive'
          )}
        >
          <header>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-400">
              Secure workspace
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900">
              Create your account
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Set up your operator workspace and connect the Polygon wallet that receives settled USDT.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-2">
            {error && (
              <div
                role="alert"
                className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {error}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label htmlFor="username" className={labelClass}>
                Username
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                className={inputClass}
              />
              <p className={hintClass}>Must be between 3 and 50 characters.</p>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="email" className={labelClass}>
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="password" className={labelClass}>
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters with letters and numbers"
                  className={cn(inputClass, 'pr-16')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={cn(
                    'absolute right-3 top-1/2 -translate-y-1/2',
                    'text-sm font-semibold text-sky-600',
                    'rounded-lg px-1 py-0.5',
                    'hover:text-sky-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40'
                  )}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <p className={hintClass}>
                Use at least eight characters with both letters and numbers.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="walletAddress" className={labelClass}>
                USDT Polygon Wallet
              </label>
              <input
                id="walletAddress"
                type="text"
                autoComplete="off"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                placeholder="0x..."
                className={inputClass}
              />
              <p className={hintClass}>
                This is the wallet that receives your net settled payouts.
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                'mt-2 w-full rounded-xl bg-sky-500 py-3.5 text-base font-semibold text-white',
                'shadow-lg shadow-sky-500/35',
                'transition hover:bg-sky-600 hover:shadow-sky-500/45',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2',
                'disabled:pointer-events-none disabled:opacity-60'
              )}
            >
              {isLoading ? 'Creating Account...' : 'Register'}
            </button>
          </form>

          <footer className="mt-8 flex flex-col items-center gap-4">
            <div className="h-px w-full bg-gray-200" />
            <p className="text-center text-sm text-gray-600">
              Already onboarded?{' '}
              <Link
                to="/login"
                className="font-medium text-sky-600 hover:text-sky-700 focus:outline-none focus-visible:underline"
              >
                Login here
              </Link>
            </p>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default Register;
